import * as React from 'react';
import * as AccordionPrimitive from '@radix-ui/react-accordion';
import { ChevronRight } from 'lucide-react';

import { cn } from '../../lib/utils';
import useI18n from '../../hooks/useI18n';
import { useTreeFocus } from './useTreeFocus';
import type { TreeItem, TreeViewProps } from './types';

const findItemPath = (
  nodes: TreeItem[],
  targetId: string,
  trail: TreeItem[] = [],
): TreeItem[] | null => {
  for (const node of nodes) {
    const nextTrail = [...trail, node];
    if (node.id === targetId) {
      return nextTrail;
    }
    if (node.children?.length) {
      const result = findItemPath(node.children, targetId, nextTrail);
      if (result) {
        return result;
      }
    }
  }
  return null;
};

type AccordionRootElement = React.ElementRef<typeof AccordionPrimitive.Root>;

export const TreeView = React.forwardRef<AccordionRootElement, TreeViewProps>(
  (
    {
      items,
      className,
      expandedIds: controlledExpandedIds,
      onExpandedChange,
      selectedId,
      onSelect,
      expandOnSelect = true,
      toggleOnSelect = false,
      selectOnChevron = false,
      collapseOthersOnSelect = false,
      scrollToSelected = false,
    },
    ref,
  ) => {
    const { t } = useI18n();
    const [internalExpandedIds, setInternalExpandedIds] = React.useState<
      string[]
    >([]);
    const isExpandedControlled = controlledExpandedIds !== undefined;
    const expandedIds = controlledExpandedIds ?? internalExpandedIds;
    const expandedIdsRef = React.useRef(expandedIds);
    React.useEffect(() => {
      expandedIdsRef.current = expandedIds;
    }, [expandedIds]);
    const updateExpandedIds = React.useCallback(
      (updater: React.SetStateAction<string[]>) => {
        const nextValue =
          typeof updater === 'function'
            ? (updater as (prev: string[]) => string[])(expandedIdsRef.current)
            : updater;

        if (!isExpandedControlled) {
          setInternalExpandedIds(nextValue);
        }

        onExpandedChange?.(nextValue);
      },
      [isExpandedControlled, onExpandedChange],
    );
    const expandedSet = React.useMemo(
      () => new Set(expandedIds),
      [expandedIds],
    );
    const [internalSelected, setInternalSelected] = React.useState<
      string | null
    >(selectedId ?? null);

    const currentSelectedId = selectedId ?? internalSelected;
    const toggleItem = React.useCallback(
      (id: string) => {
        updateExpandedIds((prev) => {
          if (prev.includes(id)) {
            return prev.filter((itemId) => itemId !== id);
          }
          return [...prev, id];
        });
      },
      [updateExpandedIds],
    );

    const expandItem = React.useCallback(
      (id: string) => {
        updateExpandedIds((prev) => (prev.includes(id) ? prev : [...prev, id]));
      },
      [updateExpandedIds],
    );

    const handleSelect = React.useCallback(
      (item: TreeItem, options?: { skipExpandHandling?: boolean }) => {
        const { skipExpandHandling = false } = options ?? {};
        if (item.disabled) return;

        if (selectedId === undefined) {
          setInternalSelected(item.id);
        }

        const shouldHandleExpand =
          !collapseOthersOnSelect &&
          !skipExpandHandling &&
          expandOnSelect &&
          item.children?.length;

        if (shouldHandleExpand) {
          if (toggleOnSelect) {
            toggleItem(item.id);
          } else {
            expandItem(item.id);
          }
        }

        onSelect?.(item);
      },
      [
        collapseOthersOnSelect,
        expandItem,
        expandOnSelect,
        onSelect,
        selectedId,
        toggleItem,
        toggleOnSelect,
      ],
    );

    const { focusTargetId, handleKeyDown, itemRefs } = useTreeFocus({
      items,
      expandedSet,
      currentSelectedId,
      expandItem,
      toggleItem,
      handleSelect,
    });

    const collapseToSelectedPath = React.useCallback(
      (targetId?: string | null) => {
        if (!collapseOthersOnSelect) {
          return;
        }
        if (!targetId) {
          updateExpandedIds([]);
          return;
        }
        const path = findItemPath(items, targetId);
        if (!path) {
          updateExpandedIds([]);
          return;
        }
        const idsToExpand = path
          .filter((pathItem) => pathItem.children?.length)
          .map((pathItem) => pathItem.id);
        updateExpandedIds(idsToExpand);
      },
      [collapseOthersOnSelect, items, updateExpandedIds],
    );

    React.useEffect(() => {
      if (!collapseOthersOnSelect) return;
      collapseToSelectedPath(currentSelectedId);
    }, [collapseOthersOnSelect, currentSelectedId, collapseToSelectedPath]);

    React.useEffect(() => {
      if (!scrollToSelected || !currentSelectedId) return;

      const element = itemRefs.current.get(currentSelectedId);
      if (!element) return;

      const frameId = window.requestAnimationFrame(() => {
        element.scrollIntoView({
          block: 'nearest',
          inline: 'nearest',
          behavior: 'smooth',
        });
      });

      return () => window.cancelAnimationFrame(frameId);
    }, [currentSelectedId, expandedIds, scrollToSelected, items, itemRefs]);

    const renderItems = React.useCallback(
      (nodes: TreeItem[], depth = 0): React.ReactNode =>
        nodes.map((node) => {
          const hasChildren = !!node.children?.length;
          const isExpanded = expandedSet.has(node.id);
          const isSelected = currentSelectedId === node.id;
          const isDisabled = !!node.disabled;

          return (
            <AccordionPrimitive.Item
              key={node.id}
              value={node.id}
              data-disabled={isDisabled ? '' : undefined}
              className='eye:w-full eye:list-none eye:first:mt-8'
            >
              <AccordionPrimitive.Header asChild>
                <div
                  className={cn(
                    'eye:flex eye:w-full eye:min-w-0 eye:cursor-pointer eye:items-start eye:gap-8 eye:rounded-md eye:px-12 eye:py-8 eye:whitespace-nowrap eye:outline-offset-3 eye:outline-transparent eye:focus-visible:outline-2 eye:focus-visible:outline-black',
                    isSelected
                      ? 'eye:bg-black eye:text-white'
                      : 'eye:text-grey-120 eye:hover:bg-grey-20',
                    isDisabled &&
                      'eye:pointer-events-none eye:text-grey-disabled',
                  )}
                  role='treeitem'
                  aria-selected={isSelected}
                  aria-expanded={hasChildren ? isExpanded : undefined}
                  aria-disabled={isDisabled}
                  aria-level={depth + 1}
                  tabIndex={node.id === focusTargetId && !isDisabled ? 0 : -1}
                  ref={(element) => {
                    if (element) {
                      itemRefs.current.set(node.id, element);
                    } else {
                      itemRefs.current.delete(node.id);
                    }
                  }}
                  onKeyDown={(event) => handleKeyDown(event, node)}
                  onClick={() => handleSelect(node)}
                >
                  {hasChildren ? (
                    <AccordionPrimitive.Trigger asChild disabled={isDisabled}>
                      <div
                        aria-label={
                          isExpanded
                            ? t('collapseTreeItem')
                            : t('expandTreeItem')
                        }
                        onClick={(event) => {
                          event.stopPropagation();
                          if (selectOnChevron) {
                            handleSelect(node, { skipExpandHandling: true });
                          }
                        }}
                        className='eye:-ml-4 eye:shrink-0 eye:cursor-pointer eye:rounded-md'
                      >
                        <ChevronRight
                          className={cn(
                            'eye:flex eye:h-lh eye:shrink-0 eye:items-center eye:transition-transform eye:duration-200',
                            isExpanded && 'eye:rotate-90',
                          )}
                        />
                      </div>
                    </AccordionPrimitive.Trigger>
                  ) : null}

                  <div className='eye:min-w-0 eye:flex-1'>{node.element}</div>
                </div>
              </AccordionPrimitive.Header>

              {hasChildren ? (
                <AccordionPrimitive.Content asChild>
                  <ul
                    role='group'
                    className='eye:ml-[18px] eye:flex eye:flex-col eye:gap-2xs eye:overflow-hidden eye:border-l-1 eye:border-grey-20 eye:pl-8 eye:focus-within:overflow-visible eye:data-[state=closed]:animate-accordion-up eye:data-[state=open]:animate-accordion-down'
                  >
                    {renderItems(node.children!, depth + 1)}
                  </ul>
                </AccordionPrimitive.Content>
              ) : null}
            </AccordionPrimitive.Item>
          );
        }),
      [
        currentSelectedId,
        expandedSet,
        focusTargetId,
        handleKeyDown,
        handleSelect,
        itemRefs,
        selectOnChevron,
        t,
      ],
    );

    return (
      <AccordionPrimitive.Root
        ref={ref}
        type='multiple'
        role='tree'
        value={expandedIds}
        onValueChange={updateExpandedIds}
        className={cn('eye:space-y-4', className)}
      >
        {renderItems(items)}
      </AccordionPrimitive.Root>
    );
  },
);

TreeView.displayName = 'TreeView';
